export interface ChainConfig {
  chainId: number;
  name: string;
  explorer: string;
  apiUrl: string;
  apiKey?: string;
  enabled: boolean;
  rpcUrl: string;
}

export interface ABIFunction {
  name: string;
  type: string;
  inputs: Array<{
    name: string;
    type: string;
    components?: Array<{
      name: string;
      type: string;
    }>;
  }>;
  outputs: Array<{
    name: string;
    type: string;
  }>;
  stateMutability: string;
}

export interface ContractABI {
  contractAddress: string;
  chainId: number;
  abi: ABIFunction[];
  verified: boolean;
  contractName?: string;
  implementation?: string; // For proxy contracts
}

// Supported chains configuration
export const SUPPORTED_CHAINS: ChainConfig[] = [
  {
    chainId: 11155111,
    name: "Sepolia Testnet",
    explorer: "sepolia.etherscan.io",
    apiUrl: "https://api.etherscan.io/v2/api",
    enabled: true,
    rpcUrl: "https://lb.drpc.org/sepolia/AnRM4mK1tEyphrn_jexSLbpLCWW7uMMR76KguivZK8k9"
  },
  {
    chainId: 1,
    name: "Ethereum Mainnet",
    explorer: "etherscan.io",
    apiUrl: "https://api.etherscan.io/v2/api",
    enabled: false,
    rpcUrl: "https://eth.drpc.org"
  },
  {
    chainId: 137,
    name: "Polygon",
    explorer: "polygonscan.com",
    apiUrl: "https://api.etherscan.io/v2/api",
    enabled: false,
    rpcUrl: "https://polygon.drpc.org"
  },
  {
    chainId: 8453,
    name: "Base",
    explorer: "basescan.org", 
    apiUrl: "https://api.etherscan.io/v2/api",
    enabled: false,
    rpcUrl: "https://base.drpc.org"
  },
  {
    chainId: 42161,
    name: "Arbitrum One",
    explorer: "arbiscan.io",
    apiUrl: "https://api.etherscan.io/v2/api", 
    enabled: false,
    rpcUrl: "https://arbitrum.drpc.org"
  }
];

/**
 * Validates if an address is a valid Ethereum address
 */
export function isValidAddress(address: string): boolean {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

/**
 * Fetches contract ABI from blockchain explorer
 */
export async function fetchContractABI(
  contractAddress: string, 
  chainId: number
): Promise<ContractABI> {
  if (!isValidAddress(contractAddress)) {
    throw new Error('Invalid contract address format');
  }

  const chain = SUPPORTED_CHAINS.find(c => c.chainId === chainId);
  if (!chain) {
    throw new Error(`Chain ID ${chainId} not supported`);
  }

  if (!chain.enabled) {
    throw new Error(`${chain.name} is not currently supported`);
  }

  try {
    const useServer = (import.meta.env.VITE_USE_SERVER_ABI === 'true') ||
      (typeof window !== 'undefined' && /vercel\.app$/.test(window.location.hostname));

    if (useServer) {
      // Use Vercel serverless function for secure ABI fetching
      const apiUrl = `/api/abi?address=${encodeURIComponent(contractAddress)}&chainId=${encodeURIComponent(String(chainId))}`;
      const response = await fetch(apiUrl);
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server ABI fetch failed: ${response.status} ${text}`);
      }
      const data = await response.json();
      const functions = data.abi as ABIFunction[];
      return {
        contractAddress,
        chainId,
        abi: functions,
        verified: !!data.verified,
        contractName: data.contractName,
      };
    }

    // Fallback: client-side direct explorer fetch (local dev)
    const url = `${chain.apiUrl}?chainid=${chainId}&module=contract&action=getabi&address=${contractAddress}&apikey=${import.meta.env.VITE_ETHERSCAN_API_KEY}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (data.status === '0') {
      if (data.message === 'Contract source code not verified') {
        throw new Error('Contract not verified on explorer');
      }
      throw new Error(data.result || 'Failed to fetch ABI');
    }
    const abi = JSON.parse(data.result);
    const functions = abi.filter((item: any) => item.type === 'function') as ABIFunction[];
    if (functions.length === 0) {
      throw new Error('No functions found in contract ABI');
    }
    return {
      contractAddress,
      chainId,
      abi: functions,
      verified: true,
      contractName: extractContractName(abi)
    };

  } catch (error) {
    console.error('ABI fetch error:', error);
    
    if (error instanceof Error) {
      throw error;
    }
    
    throw new Error('Failed to fetch contract ABI');
  }
}

/**
 * Validates manually entered ABI
 */
export function validateABI(abiJson: string): ABIFunction[] {
  try {
    const abi = JSON.parse(abiJson);
    
    if (!Array.isArray(abi)) {
      throw new Error('ABI must be an array');
    }

    const functions = abi.filter(item => item.type === 'function');
    
    if (functions.length === 0) {
      throw new Error('No functions found in ABI');
    }

    // Validate function structure
    for (const func of functions) {
      if (!func.name || typeof func.name !== 'string') {
        throw new Error('Invalid function name');
      }
      
      if (!Array.isArray(func.inputs)) {
        throw new Error(`Function ${func.name} has invalid inputs`);
      }
      
      if (!Array.isArray(func.outputs)) {
        throw new Error(`Function ${func.name} has invalid outputs`);
      }
    }

    return functions;
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error('Invalid JSON format');
    }
    throw error;
  }
}

/**
 * Extracts contract name from ABI metadata
 */
function extractContractName(abi: any[]): string | undefined {
  // Look for constructor or contract name in metadata
  const constructor = abi.find(item => item.type === 'constructor');
  return constructor?.name || undefined;
}

/**
 * Generates function signature from ABI function
 */
export function generateFunctionSignature(func: ABIFunction): string {
  // Recursively format parameter types, expanding nested tuple/tuple[] components
  const formatComponent = (comp: { type: string; components?: any[] }): string => {
    if (comp.type.startsWith('tuple')) {
      const inner = (comp.components || []).map(formatComponent).join(',');
      return comp.type.endsWith('[]') ? `(${inner})[]` : `(${inner})`;
    }
    return comp.type;
  };

  const formatInput = (input: ABIFunction['inputs'][number]): string => {
    if (input.type.startsWith('tuple')) {
      const inner = (input.components || []).map(formatComponent).join(',');
      return input.type.endsWith('[]') ? `(${inner})[]` : `(${inner})`;
    }
    return input.type;
  };

  const inputs = func.inputs.map(formatInput).join(',');
  
  return `${func.name}(${inputs})`;
}

/**
 * Gets all available functions from contract ABI
 */
export function getAvailableFunctions(contractABI: ContractABI): Array<{
  name: string;
  signature: string;
  inputs: ABIFunction['inputs'];
  stateMutability: string;
}> {
  return contractABI.abi.map(func => ({
    name: func.name,
    signature: generateFunctionSignature(func),
    inputs: func.inputs,
    stateMutability: func.stateMutability
  }));
}

/**
 * Finds function by name in contract ABI
 */
export function findFunctionByName(contractABI: ContractABI, functionName: string): ABIFunction | undefined {
  return contractABI.abi.find(func => func.name === functionName);
}